import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { useCallback, useState } from 'react';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import { useTranslation } from 'react-i18next';
import { environmentAtom } from '@data/fms/environment/states';
import type { TasksItem } from '@data/fms/schedule/types';
import { useAtomCallback } from 'jotai/utils';

export type Tasks = {
  total: number;
  tasks: TasksItem[];
};

/**
 * 指定車両の特定期間のタスク一覧取得API
 */
export const useTasksAPI = (): {
  state: APIState;
  getTasks: (
    vehicleId: string,
    fromTime: string,
    toTime: string,
    offset: number,
    limit?: number,
  ) => Promise<Tasks | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getHasScope = useHasScope();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const getTasksAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      fromTime: string,
      toTime: string,
      offset: number,
      limit: number,
    ): Promise<Result<Tasks, AxiosResponse>> => {
      try {
        const params = new URLSearchParams();
        params.append('exclude', 'unstarted');
        params.append('limit', limit.toString());
        params.append('actual_time_from', fromTime);
        params.append('actual_time_to', toTime);
        params.append('order', 'descending');
        params.append('offset', offset.toString());
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/schedules/tasks?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getTasks = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        fromTime: string,
        toTime: string,
        offset: number,
        limit: number | undefined = 100,
      ) => {
        setState('loading');
        if (!getHasScope(SCOPES.DescribeSchedule)) {
          setState('hasError');
          return null;
        }
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.get_tasks', { status: 'failed' }),
          });
          setState('hasError');
          return null;
        }
        const res = await getTasksAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          fromTime,
          toTime,
          offset,
          Number(limit),
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_tasks', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, getTasksAPI, getHasScope, t, getErrorMessage],
    ),
  );

  return {
    state,
    getTasks,
  };
};
